<template>
  <section class="cashbox-maintenance d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Cajas</h2>
        <p class="text-muted mb-0">Cada caja pertenece a una sucursal y configura su punto de expedición operativo.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" @click="loadRows" :disabled="loading">Recargar</button>
    </header>

    <div class="summary-grid">
      <div class="summary-item">
        <span>Sucursales</span>
        <strong>{{ sucursales.length }}</strong>
      </div>
      <div class="summary-item">
        <span>Cajas activas</span>
        <strong>{{ activeRowsCount }}</strong>
      </div>
      <div class="summary-item">
        <span>Predeterminadas</span>
        <strong>{{ defaultRowsCount }}</strong>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <form class="row g-3" @submit.prevent="saveRow">
          <div class="col-md-3">
            <label class="form-label">Sucursal</label>
            <select v-model.number="form.sucursal_id" class="form-select" @change="onSucursalChange">
              <option :value="null">Seleccione</option>
              <option v-for="row in sucursales" :key="row.sucursal_id" :value="row.sucursal_id">
                {{ row.nombre_establecimiento }} ({{ row.sucursal_id }})
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Punto de expedición</label>
            <select v-model.number="form.punto_id" class="form-select" :disabled="!form.sucursal_id">
              <option :value="null">Seleccione</option>
              <option v-for="row in puntosDisponibles" :key="row.punto_id" :value="row.punto_id">
                {{ row.codigo_punto_expedicion }} - {{ row.nombre }}
              </option>
            </select>
            <small v-if="form.sucursal_id && !puntosDisponibles.length" class="text-warning">
              La sucursal no tiene puntos fiscales configurados.
            </small>
          </div>
          <div class="col-md-2">
            <label class="form-label">Código caja</label>
            <input v-model="form.codigo_caja" class="form-control" type="text" maxlength="20" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Nombre</label>
            <input v-model="form.nombre" class="form-control" type="text" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Estado</label>
            <select v-model="form.estado" class="form-select">
              <option value="ACTIVA">Activa</option>
              <option value="INACTIVA">Inactiva</option>
              <option value="MANTENIMIENTO">Mantenimiento</option>
            </select>
          </div>
          <div class="col-md-3 d-flex align-items-end">
            <div class="form-check me-3">
              <input id="cajaActiva" v-model="form.activo" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="cajaActiva">Habilitada</label>
            </div>
            <div class="form-check">
              <input id="cajaPredeterminada" v-model="form.predeterminada" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="cajaPredeterminada">Predeterminada</label>
            </div>
          </div>
          <div class="col-12 d-flex gap-2 flex-wrap">
            <button class="btn btn-dark" :disabled="saving || !canSave">{{ saving ? 'Guardando...' : submitLabel }}</button>
            <button class="btn btn-outline-secondary" type="button" @click="resetForm">Limpiar</button>
          </div>
        </form>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body border-bottom">
        <div class="row g-3 align-items-end">
          <div class="col-md-5">
            <label class="form-label">Filtrar por sucursal</label>
            <select v-model.number="filters.sucursal_id" class="form-select">
              <option :value="null">Todas las sucursales</option>
              <option v-for="row in sucursales" :key="'filter-'+row.sucursal_id" :value="row.sucursal_id">
                {{ row.nombre_establecimiento }} ({{ row.sucursal_id }})
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Estado</label>
            <select v-model="filters.estado" class="form-select">
              <option value="">Todos</option>
              <option value="ACTIVA">Activas</option>
              <option value="INACTIVA">Inactivas</option>
              <option value="MANTENIMIENTO">Mantenimiento</option>
            </select>
          </div>
        </div>
      </div>
      <div class="cashbox-card-list">
        <article v-for="row in displayedRows" :key="'card-'+row.caja_id" class="cashbox-card">
          <div class="d-flex justify-content-between gap-3">
            <div>
              <span class="small text-muted">#{{ row.caja_id }} · {{ row.codigo_caja }}</span>
              <h3>{{ row.nombre }}</h3>
              <p>{{ row.nombre_establecimiento || `Sucursal ${row.sucursal_id}` }}</p>
            </div>
            <span class="status-pill" :class="statusClass(row)">{{ statusLabel(row) }}</span>
          </div>
          <div class="cashbox-card-meta">
            <span><i class="bi bi-diagram-3 me-1"></i>{{ pointLabel(row) }}</span>
            <span v-if="row.predeterminada"><i class="bi bi-star-fill me-1"></i>Predeterminada</span>
          </div>
          <div class="cashbox-card-actions">
            <button class="btn btn-outline-dark btn-sm" @click="editRow(row)">Editar</button>
            <button
              class="btn btn-outline-danger btn-sm"
              :disabled="row.activo === false || row.estado === 'INACTIVA'"
              @click="inactivateRow(row)"
            >
              Inactivar
            </button>
          </div>
        </article>
      </div>
      <div class="table-responsive cashbox-table">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Sucursal</th>
              <th>Código</th>
              <th>Nombre</th>
              <th>Punto</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center text-muted py-4">Cargando...</td>
            </tr>
            <tr v-else-if="!displayedRows.length">
              <td colspan="7" class="text-center text-muted py-4">No hay cajas cargadas.</td>
            </tr>
            <tr v-for="row in displayedRows" :key="row.caja_id">
              <td>{{ row.caja_id }}</td>
              <td>{{ row.nombre_establecimiento || `Sucursal ${row.sucursal_id}` }}</td>
              <td>{{ row.codigo_caja }}</td>
              <td>
                {{ row.nombre }}
                <span v-if="row.predeterminada" class="badge bg-dark ms-2">Pred.</span>
              </td>
              <td>{{ pointLabel(row) }}</td>
              <td><span class="status-pill" :class="statusClass(row)">{{ statusLabel(row) }}</span></td>
              <td class="text-end">
                <div class="d-inline-flex gap-2">
                  <button class="btn btn-outline-dark btn-sm" @click="editRow(row)">Editar</button>
                  <button
                    class="btn btn-outline-danger btn-sm"
                    :disabled="row.activo === false || row.estado === 'INACTIVA'"
                    @click="inactivateRow(row)"
                  >
                    Inactivar
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import maintenanceFiscalService from '@/services/maintenanceFiscalService'
import operationalContextService from '@/services/operationalContextService'

const loading = ref(false)
const saving = ref(false)
const rows = ref([])
const sucursales = ref([])
const puntosPorSucursal = ref({})
const filters = reactive({
  sucursal_id: null,
  estado: ''
})
const form = reactive({
  caja_id: null,
  sucursal_id: null,
  punto_id: null,
  codigo_caja: '',
  nombre: '',
  estado: 'ACTIVA',
  activo: true,
  predeterminada: false
})

const submitLabel = computed(() => form.caja_id ? 'Actualizar caja' : 'Crear caja')
const puntosDisponibles = computed(() => puntosPorSucursal.value[String(form.sucursal_id || '')] || [])
const displayedRows = computed(() => rows.value.filter((row) => {
  const matchesSucursal = !filters.sucursal_id || Number(row.sucursal_id) === Number(filters.sucursal_id)
  const matchesEstado = !filters.estado || String(row.estado || '').toUpperCase() === filters.estado
  return matchesSucursal && matchesEstado
}))
const activeRowsCount = computed(() => rows.value.filter((row) => row.activo !== false && row.estado === 'ACTIVA').length)
const defaultRowsCount = computed(() => rows.value.filter((row) => row.predeterminada === true).length)
const canSave = computed(() =>
  Boolean(form.sucursal_id && form.punto_id && String(form.codigo_caja || '').trim() && String(form.nombre || '').trim())
)

function pointLabel(row) {
  if (!row?.punto_id) return 'Sin punto asignado'
  const code = row.codigo_punto_expedicion ? `${row.codigo_punto_expedicion} - ` : ''
  return `${code}${row.punto_nombre || `Punto ${row.punto_id}`}`
}

function statusLabel(row) {
  if (row.activo === false) return 'Inactiva'
  if (row.estado === 'MANTENIMIENTO') return 'Mantenimiento'
  if (row.estado === 'INACTIVA') return 'Inactiva'
  return 'Activa'
}

function statusClass(row) {
  if (row.activo === false || row.estado === 'INACTIVA') return 'status-inactive'
  if (row.estado === 'MANTENIMIENTO') return 'status-maintenance'
  return 'status-active'
}

function resetForm() {
  form.caja_id = null
  form.sucursal_id = sucursales.value[0]?.sucursal_id || null
  form.punto_id = null
  form.codigo_caja = ''
  form.nombre = ''
  form.estado = 'ACTIVA'
  form.activo = true
  form.predeterminada = false
}

async function ensurePointsForSucursal(sucursalId) {
  const key = String(sucursalId || '')
  if (!sucursalId || puntosPorSucursal.value[key]) return
  const selectedSucursal = sucursales.value.find((row) => Number(row.sucursal_id) === Number(sucursalId))
  if (!selectedSucursal?.establecimiento_id) return
  const points = await maintenanceFiscalService.listPoints(selectedSucursal.establecimiento_id)
  puntosPorSucursal.value = {
    ...puntosPorSucursal.value,
    [key]: Array.isArray(points) ? points : []
  }
}

async function onSucursalChange() {
  form.punto_id = null
  await ensurePointsForSucursal(form.sucursal_id)
}

function editRow(row) {
  form.caja_id = row.caja_id
  form.sucursal_id = row.sucursal_id
  form.punto_id = row.punto_id || null
  form.codigo_caja = row.codigo_caja || ''
  form.nombre = row.nombre || ''
  form.estado = row.estado || 'ACTIVA'
  form.activo = row.activo !== false
  form.predeterminada = row.predeterminada === true
  ensurePointsForSucursal(form.sucursal_id)
}

async function loadRows() {
  loading.value = true
  try {
    rows.value = await operationalContextService.listCajas() || []
  } catch (error) {
    console.error('Error cargando cajas', error)
    window.alert.error(error.userMessage || error.response?.data?.message || 'No se pudo cargar cajas')
  } finally {
    loading.value = false
  }
}

async function loadSucursales() {
  const data = await operationalContextService.listSucursales()
  sucursales.value = Array.isArray(data) ? data : []
  if (!form.sucursal_id && sucursales.value.length) {
    form.sucursal_id = sucursales.value[0].sucursal_id
    await ensurePointsForSucursal(form.sucursal_id)
  }
}

async function saveRow() {
  if (!canSave.value) {
    window.alert.warning('Complete sucursal, punto de expedición, código y nombre de caja.')
    return
  }
  saving.value = true
  try {
    const payload = {
      sucursal_id: form.sucursal_id,
      punto_id: form.punto_id,
      codigo_caja: form.codigo_caja,
      nombre: form.nombre,
      estado: form.estado,
      activo: form.activo,
      predeterminada: form.predeterminada
    }
    if (form.caja_id) {
      await operationalContextService.updateCaja(form.caja_id, payload)
      window.alert.success('Caja actualizada correctamente')
    } else {
      await operationalContextService.createCaja(payload)
      window.alert.success('Caja creada correctamente')
    }
    resetForm()
    await loadRows()
  } catch (error) {
    console.error('Error guardando caja', error)
    window.alert.error(error.userMessage || error.response?.data?.message || 'No se pudo guardar la caja')
  } finally {
    saving.value = false
  }
}

async function inactivateRow(row) {
  const confirmed = await window.confirm({
    title: 'Inactivar caja',
    message: `¿Desea inactivar ${row.codigo_caja} - ${row.nombre}?`,
    confirmText: 'Inactivar',
    cancelText: 'Cancelar'
  })
  if (!confirmed) return

  saving.value = true
  try {
    await operationalContextService.updateCaja(row.caja_id, {
      ...row,
      estado: 'INACTIVA',
      activo: false,
      predeterminada: false
    })
    window.alert.success('Caja inactivada correctamente')
    await loadRows()
  } catch (error) {
    console.error('Error inactivando caja', error)
    window.alert.error(error.userMessage || error.response?.data?.message || 'No se pudo inactivar la caja')
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    await loadSucursales()
    await loadRows()
  } catch (error) {
    console.error('Error inicializando mantenimiento de cajas', error)
    window.alert.error(error.userMessage || error.response?.data?.message || 'No se pudo cargar mantenimiento de cajas')
  }
})
</script>

<style scoped>
.summary-grid {
  display: grid;
  gap: .75rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.summary-item {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: .85rem 1rem;
}

.summary-item span {
  display: block;
  color: #6b7280;
  font-size: .85rem;
}

.summary-item strong {
  color: #111827;
  font-size: 1.35rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: .78rem;
  font-weight: 700;
  padding: .24rem .6rem;
}

.status-active {
  background: #d1e7dd;
  color: #0f5132;
}

.status-inactive {
  background: #f8d7da;
  color: #842029;
}

.status-maintenance {
  background: #fff3cd;
  color: #664d03;
}

.cashbox-card-list {
  display: none;
}

.cashbox-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 1rem;
}

.cashbox-card h3 {
  font-size: 1rem;
  margin: .2rem 0 .1rem;
}

.cashbox-card p {
  color: #4b5563;
  margin: 0;
}

.cashbox-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem 1rem;
  color: #6b7280;
  margin-top: .8rem;
}

.cashbox-card-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .5rem;
  margin-top: 1rem;
}

@media (max-width: 767.98px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .cashbox-table {
    display: none;
  }

  .cashbox-card-list {
    display: grid;
    gap: .75rem;
    padding: .75rem;
  }
}
</style>
